import { useCallback } from 'react';
import {
  parseAsBoolean,
  parseAsJson,
  parseAsStringEnum,
  useQueryStates,
} from 'nuqs';
import { dayjs } from 'util/shared/date';
import {
  DEFAULT_VIX_CONFIG,
  ExpirationsDisplayType,
  VixConfig,
} from 'types/impliedVol';

/** URL-serializable shape (no Dayjs — tradeDate is reconstructed from key) */
interface SerializedVixDateEntry {
  key: string;
  color: string;
  notDisplayed?: boolean;
}

function parseSerializedDates(value: unknown): SerializedVixDateEntry[] {
  if (
    Array.isArray(value) &&
    value.every(
      (v) =>
        typeof v === 'object' &&
        v != null &&
        'key' in v &&
        typeof (v as SerializedVixDateEntry).key === 'string',
    )
  ) {
    return value as SerializedVixDateEntry[];
  }
  return [];
}

const VIX_QUERY_KEYS = {
  vix_econ: parseAsBoolean.withDefault(DEFAULT_VIX_CONFIG.showEconomicEvents),
  vix_disp: parseAsStringEnum<ExpirationsDisplayType>(
    Object.values(ExpirationsDisplayType),
  ).withDefault(DEFAULT_VIX_CONFIG.displayType),
  vix_dates: parseAsJson<SerializedVixDateEntry[]>(
    parseSerializedDates,
  ).withDefault([]),
} as const;

export function useVixConfig(): [
  VixConfig,
  (patch: Partial<VixConfig>) => void,
] {
  const [params, setParams] = useQueryStates(VIX_QUERY_KEYS);

  const config: VixConfig = {
    showEconomicEvents: params.vix_econ,
    displayType: params.vix_disp,
    additionalDates: params.vix_dates.map((e) => ({
      key: e.key,
      color: e.color,
      tradeDate: dayjs(e.key),
      notDisplayed: e.notDisplayed,
    })),
  };

  const updateConfig = useCallback(
    (patch: Partial<VixConfig>) => {
      const urlPatch: Partial<{
        vix_econ: boolean;
        vix_disp: ExpirationsDisplayType;
        vix_dates: SerializedVixDateEntry[];
      }> = {};
      if (patch.showEconomicEvents != null) {
        urlPatch.vix_econ = patch.showEconomicEvents;
      }
      if (patch.displayType != null) {
        urlPatch.vix_disp = patch.displayType;
      }
      if (patch.additionalDates != null) {
        urlPatch.vix_dates = patch.additionalDates.map(
          ({ key, color, notDisplayed }) => ({
            key,
            color,
            ...((notDisplayed ?? false) ? { notDisplayed } : {}),
          }),
        );
      }
      void setParams(urlPatch);
    },
    [setParams],
  );

  return [config, updateConfig];
}
